import { RELATORIOS_DISPONIVEIS } from '@pieltec/contracts';
// apps/web/src/pages/relatorios/relatorios-index-page.tsx — Fase 03, Plano 10 (REL-01, REL-02).
import React, { useEffect, useState } from 'react';
import {
  FileText,
  BarChart3,
  Layers,
  ArrowLeft,
  Search,
} from 'lucide-react';
import type { RelatorioDescritor } from '@pieltec/contracts';
import { RelatorioFiltroForm } from './relatorio-filtro-form';

export const RelatoriosIndexPage: React.FC = () => {
  const [tab, setTab] = useState<'relatorios' | 'analises'>('relatorios');
  const [selecionado, setSelecionado] = useState<RelatorioDescritor | null>(null);
  const [busca, setBusca] = useState('');

  const [dados, setDados] = useState<RelatorioDescritor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let ativo = true;
    setCarregando(true); setFalhou(false);
    void (async () => {
      try {
        const resposta = await fetch('/api/v1/relatorios/catalogo', { credentials: 'include', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
        if (!resposta.ok) throw new Error('Falha no catálogo.');
        const itens = await resposta.json() as RelatorioDescritor[];
        if (ativo) setDados(itens);
      } catch { if (ativo) setFalhou(true); }
      finally { if (ativo) setCarregando(false); }
    })();
    return () => { ativo = false; controller.abort(); };
  }, [tentativa]);
  const catalogo = { data: dados, isPending: carregando, isError: falhou, refetch: () => setTentativa(t => t + 1) };
  const relatorios = (catalogo.data ?? []).filter(r => r.codigo.startsWith('R') && r.codigo !== 'R17');
  const analises = (catalogo.data ?? []).filter(r => r.codigo.startsWith('A'));
  const listaAtual = tab === 'relatorios' ? relatorios : analises;
  const filtrados = listaAtual.filter((r) => {
    const termo = busca.toLowerCase();
    return r.nome.toLowerCase().includes(termo) || r.codigo.toLowerCase().includes(termo) || r.descricao.toLowerCase().includes(termo);
  });

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Layers className="w-6 h-6 text-slate-800" />
            <h1 className="text-xl font-bold text-slate-900">Relatórios & Análises</h1>
          </div>
          <p className="text-xs text-slate-500">
            Consulte produção, qualidade e estoque com filtros próprios para cada relatório.
          </p>
        </div>

        {selecionado && (
          <button
            onClick={() => {
              setSelecionado(null);

            }}
            className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Voltar ao Catálogo</span>
          </button>
        )}
      </div>

      {/* Visualização de Relatório Selecionado */}
      {selecionado ? (
        <div className="space-y-6 animate-in fade-in duration-150">
          <RelatorioFiltroForm
            relatorio={selecionado}
            key={selecionado.id}
          />

        </div>
      ) : (
        /* Catálogo em Grade de Cartões */
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Tabs de Seleção */}
            <div className="flex items-center gap-2 border p-1 rounded-lg bg-slate-100/80 w-fit text-xs">
              <button
                onClick={() => {
                  setTab('relatorios');
                  setBusca('');
                }}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md font-semibold transition-colors ${
                  tab === 'relatorios' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Relatórios Operacionais ({relatorios.length})</span>
              </button>
              <button
                onClick={() => {
                  setTab('analises');
                  setBusca('');
                }}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md font-semibold transition-colors ${
                  tab === 'analises' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                <span>Análises Gerenciais ({analises.length})</span>
              </button>
            </div>

            {/* Campo de Busca Rápida */}
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                aria-label="Buscar relatórios e análises"
                placeholder="Buscar por nome, código ou palavra..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-xs bg-white text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-slate-900"
              />
            </div>
          </div>

          {catalogo.isPending && <p role="status">Carregando relatórios…</p>}
          {catalogo.isError && <div role="alert" className="rounded-lg border p-4">Não foi possível carregar o catálogo. <button className="underline" onClick={() => void catalogo.refetch()}>Tentar novamente</button></div>}
          {/* Grade de Relatórios */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtrados.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                aria-label={'Abrir ' + item.nome}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelecionado(item); } }}
                onClick={() => setSelecionado(item)}
                className="p-4 bg-white border rounded-xl shadow-xs hover:shadow-md hover:border-slate-400 cursor-pointer transition-all flex flex-col justify-between space-y-3 group"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-0.5 rounded font-mono text-[10px] font-bold bg-slate-100 text-slate-800 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                      {item.codigo}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase ${
                        item.pesado
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      }`}
                    >
                      {!RELATORIOS_DISPONIVEIS.includes(item.codigo) ? 'Em preparação' : item.emDesenvolvimento ? 'Dados parciais' : item.pesado ? 'Processamento em segundo plano' : 'Consulta direta'}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-slate-900">
                    {item.nome}
                  </h3>

                  <p className="text-xs text-slate-600 line-clamp-2">
                    {item.descricao}
                  </p>
                </div>

                <div className="pt-2 border-t flex items-center justify-between text-[11px] text-slate-400">
                  <span className="capitalize">{item.categoria}</span>
                  <span className="font-semibold text-slate-700 group-hover:underline">
                    Abrir relatório →
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
